/**
 * The control-scheme catalog. Adding a scheme means: an entry here, a renderer
 * in `@djgg/input-phaser` (`schemeToSpec` — its exhaustiveness test locks the
 * two together), tutorial/preview drawing driven by `gestures`, and a settings
 * option (the registry derives its option list from this catalog).
 */
import type {
  ControlSchemeDef,
  ControlSchemeId,
  GestureHint,
  SchemeParams,
} from './types.js';

const DRAG_AXIS = {
  key: 'dragAxis',
  label: 'Drag direction',
  options: [
    { id: 'y', label: 'Up / down' },
    { id: 'x', label: 'Left / right' },
    { id: 'both', label: 'Any direction' },
  ],
  fallback: 'both',
} as const;

const DPAD_STYLE = {
  key: 'dpadStyle',
  label: 'Pad style',
  options: [
    { id: '4dir', label: '4 directions' },
    { id: 'horizontal', label: 'Left / right only' },
  ],
  fallback: '4dir',
} as const;

const STICK_MODE = {
  key: 'stickMode',
  label: 'Joystick placement',
  options: [
    { id: 'dynamic', label: 'Appears under your finger' },
    { id: 'static', label: 'Fixed in the corner' },
  ],
  fallback: 'dynamic',
} as const;

const MOVE_CONTROL = {
  key: 'moveControl',
  label: 'Movement control',
  options: [
    { id: 'stick', label: 'Joystick' },
    { id: 'dpad', label: 'D-pad' },
  ],
  fallback: 'stick',
} as const;

const ACTION_EXTENT = {
  key: 'actionExtent',
  label: 'Action area',
  options: [
    { id: 'full', label: 'Everywhere else' },
    { id: 'right', label: 'The right half' },
  ],
  fallback: 'full',
} as const;

/** Long-press threshold. The option ids ARE the millisecond values, so
 *  {@link holdThresholdMs} is the only conversion and there is no second copy
 *  of these numbers anywhere. */
const HOLD_DELAY = {
  key: 'holdDelay',
  label: 'Long-press time',
  options: [
    { id: '250', label: 'Quick (0.25s)' },
    { id: '350', label: 'Standard (0.35s)' },
    { id: '500', label: 'Deliberate (0.5s)' },
  ],
  fallback: '350',
} as const;

/** The `zone-fire` long-press threshold in ms for a set of dense params. */
export function holdThresholdMs(params: SchemeParams): number {
  const chosen = HOLD_DELAY.options.find((o) => o.id === params[HOLD_DELAY.key]);
  return Number(chosen?.id ?? HOLD_DELAY.fallback);
}

const dragKind = (params: SchemeParams): GestureHint['kind'] => {
  const axis = params['dragAxis'] ?? DRAG_AXIS.fallback;
  return axis === 'x' ? 'drag-x' : axis === 'y' ? 'drag-y' : 'drag-both';
};

export const CONTROL_SCHEMES: Readonly<Record<ControlSchemeId, ControlSchemeDef>> = {
  'stick-buttons': {
    id: 'stick-buttons',
    label: 'Joystick + buttons',
    description: 'A movement joystick on the left, action buttons on the right.',
    genreHint: 'Free movement — chases, top-down arenas, shooters.',
    compatibleWith: ['keyboard', 'both'],
    params: [STICK_MODE],
    gestures: () => [
      { zone: 'left', kind: 'stick', text: 'Drag to move' },
      { zone: 'buttons', kind: 'tap', text: 'Tap {action}' },
    ],
  },
  'dpad-buttons': {
    id: 'dpad-buttons',
    label: 'D-pad + buttons',
    description: 'Direction pad on the left, action buttons on the right.',
    genreHint: 'Grid & platform movement — platformers, mazes, falling blocks.',
    compatibleWith: ['keyboard', 'both'],
    params: [DPAD_STYLE],
    gestures: () => [
      { zone: 'left', kind: 'dpad', text: 'Press to move' },
      { zone: 'buttons', kind: 'tap', text: 'Tap {action}' },
    ],
  },
  'zones-lr': {
    id: 'zones-lr',
    label: 'Drag & tap zones',
    description: 'Drag the left half to steer, tap the right half to act.',
    genreHint: 'Aim-and-shoot and charge-release games.',
    compatibleWith: ['keyboard', 'pointer', 'both'],
    params: [DRAG_AXIS],
    gestures: (params) => [
      { zone: 'left', kind: dragKind(params), text: 'Drag to steer' },
      { zone: 'right', kind: 'tap', text: 'Tap to {action}' },
    ],
  },
  'zone-fire': {
    id: 'zone-fire',
    label: 'Fire anywhere',
    description:
      'One anchored movement control; the rest of the screen fires. Hold for the second action.',
    genreHint: 'Shooters and one-button action — scrolling shmups, runners, dodge-and-shoot.',
    compatibleWith: ['keyboard', 'both'],
    // `DPAD_STYLE` is the SAME param object `dpad-buttons` uses — a one-axis
    // game (e.g. a Galaga-style fighter reading only left/right) can drop the
    // dead up/down arrows without a second way to say "horizontal pad".
    params: [MOVE_CONTROL, DPAD_STYLE, ACTION_EXTENT, HOLD_DELAY],
    gestures: (params) => [
      {
        zone: 'left',
        kind: params[MOVE_CONTROL.key] === 'dpad' ? 'dpad' : 'stick',
        text:
          params[MOVE_CONTROL.key] === 'dpad'
            ? 'Press the D-pad to move'
            : 'Drag the joystick to move',
      },
      {
        zone: params[ACTION_EXTENT.key] === 'right' ? 'right' : 'full',
        kind: 'tap',
        text:
          params[ACTION_EXTENT.key] === 'right'
            ? 'Tap the right half to {action}'
            : 'Tap anywhere to {action}',
      },
      // Dropped by the controls card when the game declares no second action —
      // there the press simply holds {action}, which the tap row already says.
      { zone: 'full', kind: 'hold', text: 'Hold to {action2}' },
    ],
  },
  'drag-track': {
    id: 'drag-track',
    label: 'Drag to move',
    description: 'Your piece follows your finger anywhere on the screen.',
    genreHint: 'Paddles, lanes, catchers.',
    compatibleWith: ['pointer', 'both'],
    params: [],
    gestures: () => [{ zone: 'full', kind: 'drag-y', text: 'Drag to move' }],
  },
  'tap-play': {
    id: 'tap-play',
    label: 'Tap the board',
    description: 'Play by tapping and dragging pieces directly.',
    genreHint: 'Puzzles, match games, boards.',
    compatibleWith: ['pointer', 'both'],
    params: [],
    gestures: () => [{ zone: 'full', kind: 'tap', text: 'Tap and drag the pieces' }],
  },
  'tap-fire': {
    id: 'tap-fire',
    label: 'Tap to fire',
    description: 'Tap a target to fire; hold for an automatic weapon.',
    genreHint: 'Front-view galleries, rail shooters, and fixed-camera target games.',
    compatibleWith: ['pointer', 'both'],
    params: [],
    gestures: () => [
      { zone: 'full', kind: 'tap', text: 'Tap a target to fire' },
      { zone: 'full', kind: 'hold', text: 'Hold for automatic fire' },
    ],
  },
  'tap-target': {
    id: 'tap-target',
    label: 'Tap to move',
    description: 'Tap the playfield to move there; tap again for the action.',
    genreHint: 'Clear-path movement — tactical runners, arena games, puzzle action.',
    compatibleWith: ['pointer', 'both'],
    params: [],
    gestures: () => [
      { zone: 'full', kind: 'tap', text: 'Tap where you want to move' },
      { zone: 'full', kind: 'double-tap', text: 'Double-tap to {action2}' },
    ],
  },
  'hold-target': {
    id: 'hold-target',
    label: 'Hold to move',
    description: 'Hold or drag toward a point to steer there; tap to act.',
    genreHint: 'Precision flight and action games with open playfields.',
    compatibleWith: ['pointer', 'both'],
    params: [],
    gestures: () => [
      { zone: 'full', kind: 'hold', text: 'Hold toward where you want to move' },
      { zone: 'full', kind: 'tap', text: 'Tap to {action}' },
    ],
  },
  'relative-steer': {
    id: 'relative-steer',
    label: 'Touch-relative steering',
    description: 'Touch around your character to steer relative to its position.',
    genreHint: 'Top-down movement where keeping the character visible matters.',
    compatibleWith: ['pointer', 'both'],
    params: [],
    gestures: () => [
      { zone: 'full', kind: 'relative', text: 'Touch around your character to move' },
      { zone: 'full', kind: 'tap', text: 'Tap to {action}' },
    ],
  },
  'tap-sides': {
    id: 'tap-sides',
    label: 'Tap either side',
    description: 'Tap left or right of your craft for one discrete turn.',
    genreHint: 'Landers and step-turn games with separate action buttons.',
    compatibleWith: ['pointer', 'both'],
    params: [],
    gestures: () => [
      { zone: 'full', kind: 'tap-sides', text: 'Tap left or right of your craft to turn' },
      { zone: 'buttons', kind: 'tap', text: 'Tap {action}' },
    ],
  },
  'gutter-dpad': {
    id: 'gutter-dpad',
    label: 'D-pad + buttons (legacy)',
    description: 'Compatibility name for the standard D-pad and action-button layout.',
    genreHint: 'Legacy settings only; new designs should choose the standard D-pad layout.',
    compatibleWith: ['keyboard', 'both'],
    params: [DPAD_STYLE],
    gestures: (params) => [
      { zone: 'gutter', kind: 'dpad', text: params[DPAD_STYLE.key] === 'horizontal' ? 'Press left or right to move' : 'Press the D-pad to move' },
      { zone: 'buttons', kind: 'tap', text: 'Tap {action}' },
    ],
  },
};

export const CONTROL_SCHEME_IDS = Object.keys(CONTROL_SCHEMES) as readonly ControlSchemeId[];

/** The scheme used when neither the creator nor the adapter picked one —
 *  today's status quo overlay. */
export const DEFAULT_SCHEME_ID: ControlSchemeId = 'stick-buttons';

export function isControlSchemeId(v: unknown): v is ControlSchemeId {
  return typeof v === 'string' && Object.hasOwn(CONTROL_SCHEMES, v);
}

/**
 * Every scheme param's current answer, read off a settings map — what the
 * studio's preview cards need, since each card renders under the creator's
 * OWN answers and every scheme's params must be available at once. The KEY
 * SET is the catalog's, so a new param reaches the previews without a second
 * list to remember; unknown or illegal values are dropped to the fallback by
 * `resolveScheme` downstream.
 */
export function schemeParamsFrom(values: Readonly<Record<string, unknown>>): SchemeParams {
  const out: Record<string, string> = {};
  for (const def of Object.values(CONTROL_SCHEMES)) {
    for (const param of def.params) {
      const v = values[param.key];
      if (typeof v === 'string' && param.options.some((o) => o.id === v)) out[param.key] = v;
    }
  }
  return out;
}

/** Keep only parameter values declared by the selected scheme. Untrusted
 *  callers use this before mapping proposal/config wire data into settings. */
export function sanitizeSchemeParams(id: ControlSchemeId, value: unknown): SchemeParams {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const input = value as Record<string, unknown>;
  return Object.fromEntries(
    CONTROL_SCHEMES[id].params.flatMap((param) => {
      const candidate = input[param.key];
      return typeof candidate === 'string' && param.options.some((option) => option.id === candidate)
        ? [[param.key, candidate]]
        : [];
    }),
  );
}
